import type { VoucherFormValues } from '@/features/voucher/voucher-form'
import { useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from '@/shared/libs/generate-path'
import { useTenantByIdQuery } from '@/entities/tenant'
import {
  useCreateVoucherMutation,
  useUpdateVoucherMutation,
  useVoucherByIdQuery,
} from '@/entities/voucher'
import { VoucherForm } from '@/features/voucher/voucher-form'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Skeleton } from '@/shared/ui/skeleton'

export function VoucherFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const params = useParams({ strict: false })
  const tenantId = params.tenantId ?? ''
  const voucherId = params.voucherId ?? ''
  const isEdit = Boolean(voucherId)

  const { data: tenantData } = useTenantByIdQuery(tenantId)
  const tenantName = tenantData?.data.name ?? ''

  const { data: existingVoucher, isLoading: isVoucherLoading } = useVoucherByIdQuery(voucherId)

  const createMutation = useCreateVoucherMutation()
  const updateMutation = useUpdateVoucherMutation()
  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const voucherListPath = scopedAdminPath(generatePath('/tenant/:tenantId/vouchers', { tenantId }))

  async function handleSubmit(values: VoucherFormValues) {
    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: voucherId, payload: values })
        showToast.success(t('voucher.updateSuccess'))
      } else {
        await createMutation.mutateAsync({ ...values, tenantId })
        showToast.success(t('voucher.createSuccess'))
      }
      void navigate({ to: voucherListPath })
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  function handleCancel() {
    void navigate({ to: voucherListPath })
  }

  const defaultValues = useMemo<Partial<VoucherFormValues>>(() => {
    if (!existingVoucher) return {}
    return {
      code: existingVoucher.code,
      name: existingVoucher.name,
      discountPercent: existingVoucher.discountPercent,
      quantity: existingVoucher.quantity,
      startDate: existingVoucher.startDate,
      endDate: existingVoucher.endDate,
      status: existingVoucher.status,
      type: existingVoucher.type,
      applicableTourIds: existingVoucher.applicableTourIds,
      applicablePoiIds: existingVoucher.applicablePoiIds,
    }
  }, [existingVoucher])

  if (isEdit && isVoucherLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-64 mb-6" />
        <div className="space-y-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title={isEdit ? t('voucher.edit') : t('voucher.create')}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(tenantName
            ? [
                {
                  label: tenantName,
                  path: scopedAdminPath(generatePath('/tenant/:tenantId', { tenantId })),
                },
              ]
            : []),
          {
            label: t('sidebar.vouchers'),
            path: scopedAdminPath(generatePath('/tenant/:tenantId/vouchers', { tenantId })),
          },
          { label: isEdit ? t('voucher.edit') : t('voucher.create') },
        ]}
        actions={
          <Button variant="outline" size="sm" onClick={handleCancel}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {t('common.back')}
          </Button>
        }
      />

      <div className="w-full">
        <VoucherForm
          key={voucherId || 'create'}
          mode={isEdit ? 'edit' : 'create'}
          tenantId={tenantId}
          defaultValues={defaultValues}
          voucher={existingVoucher}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </PageContainer>
  )
}
